import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupMessage, setSetupMessage] = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      navigate("/admin");
    } catch (err: any) {
      setError(err.message || "Credenciales invalidas. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupAdmin = async () => {
    setSetupLoading(true);
    setSetupMessage(null);
    setError(null);
    try {
      const adminEmail = "admin@masmed.cl";
      const adminPassword = "admin123";

      // Crear usuario en Supabase Auth
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: adminEmail,
        password: adminPassword,
      });

      if (signUpError) {
        if (signUpError.message.includes("User already registered")) {
          setSetupMessage("El usuario admin ya existe. Usa admin@masmed.cl / admin123");
          return;
        }
        throw new Error(signUpError.message);
      }

      if (signUpData.user) {
        // Insertar en admin_users (RLS desactivada temporalmente)
        const { error: insertError } = await supabase.from("admin_users").insert({
          id: signUpData.user.id,
          email: adminEmail,
          role: "admin",
        });

        if (insertError) {
          throw new Error(insertError.message);
        }

        setSetupMessage("Usuario admin creado correctamente. Usa: admin@masmed.cl / admin123");
      } else {
        setSetupMessage("Usuario admin ya existe. Usa: admin@masmed.cl / admin123");
      }
    } catch (err: any) {
      setError(err.message || "Error al configurar el admin.");
    } finally {
      setSetupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-masmed-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-[420px] bg-white rounded-xl shadow-lg border border-masmed-gray-200 p-8 md:p-10">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <i className="ri-settings-3-line text-white text-xl" />
          </div>
          <h1 className="text-xl font-bold text-masmed-gray-900"
          style={{ fontFamily: "'Utendo', 'Arial', sans-serif" }}>
            Panel Admin
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-masmed-gray-700 mb-1.5"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
              Correo electronico
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@ejemplo.com"
              className="w-full h-11 px-4 rounded-lg border border-masmed-gray-200 text-sm text-masmed-gray-900 placeholder-masmed-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-masmed-gray-700 mb-1.5"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
              Contrasena
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full h-11 px-4 rounded-lg border border-masmed-gray-200 text-sm text-masmed-gray-900 placeholder-masmed-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {setupMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-check-line text-green-500 text-sm mt-0.5" />
              <div className="text-sm text-green-600">
                <p className="font-medium"
                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                  {setupMessage}
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm mt-0.5" />
              <p className="text-sm text-red-600"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                {error}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                Iniciando sesion...
              </span>
            ) : (
              "Iniciar sesion"
            )}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-masmed-gray-200">
          <button
            onClick={handleSetupAdmin}
            disabled={setupLoading}
            className="w-full h-10 rounded-lg border border-masmed-gray-300 text-masmed-gray-600 text-sm font-medium hover:bg-masmed-gray-50 hover:text-masmed-gray-700 transition-colors disabled:opacity-50 cursor-pointer"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}
          >
            {setupLoading ? (
              <span className="inline-flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                Configurando...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <i className="ri-user-add-line" />
                Crear usuario admin por defecto
              </span>
            )}
          </button>
          <p className="mt-2 text-xs text-center text-masmed-gray-400"
          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
            Usa esto si no puedes iniciar sesion. Crea: admin@masmed.cl / admin123
          </p>
        </div>

        <div className="mt-6 text-center">
          <a href="/"
            className="text-sm text-masmed-gray-500 hover:text-primary transition-colors"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}
          >
            ← Volver al sitio
          </a>
        </div>
      </div>
    </div>
  );
}